'use client'

import type { CardMobileVariant } from '@/components/v3/atoms/card/card'
import { SUPPLIER_LIST_VIEW_COOKIE } from '@/utils/supplier-list-view'
import { useState } from 'react'

// Persist the preference for a year.
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

type CookieHub = {
  hasConsented?: (category: string) => boolean
}

// This is a UI-preference ("Preferences") cookie, so only persist it once the
// user has granted CookieHub preferences consent.
const hasPreferencesConsent = (): boolean => {
  const cookiehub = (window as Window & { cookiehub?: CookieHub }).cookiehub
  return Boolean(cookiehub?.hasConsented?.('preferences'))
}

/**
 * Supplier list view ('landscape' | 'vertical'), seeded from the server-read
 * cookie so the first paint matches the stored choice (no flash). The toggle
 * persists back to the cookie only when the user has granted preferences
 * consent; otherwise the choice lives for the session only.
 */
export const useSupplierListView = (initialView: CardMobileVariant) => {
  const [view, setViewState] = useState<CardMobileVariant>(initialView)

  const setView = (next: CardMobileVariant) => {
    setViewState(next)

    if (typeof document !== 'undefined' && hasPreferencesConsent()) {
      const secure = window.location.protocol === 'https:' ? '; Secure' : ''
      document.cookie = `${SUPPLIER_LIST_VIEW_COOKIE}=${next}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`
    }
  }

  return [view, setView] as const
}
